import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { STRIPE_CONFIG } from '@/lib/stripeConfig';
import { toast } from 'sonner';

interface PremiumContextType {
  isPremium: boolean;
  plan: string | null;
  subscriptionEnd: string | null;
  loading: boolean;
  foundingMembersRemaining: number;
  showUpgrade: boolean;
  setShowUpgrade: (v: boolean) => void;
  upgradeSportName: string | null;
  setUpgradeSportName: (name: string | null) => void;
  checkSubscription: () => Promise<boolean | void>;
  startCheckout: (priceId: string) => Promise<void>;
  openCustomerPortal: () => Promise<void>;
}

const FOUNDING_CAP = 200;

const PremiumContext = createContext<PremiumContextType>({
  isPremium: false,
  plan: null,
  subscriptionEnd: null,
  loading: true,
  foundingMembersRemaining: FOUNDING_CAP,
  showUpgrade: false,
  setShowUpgrade: () => {},
  upgradeSportName: null,
  setUpgradeSportName: () => {},
  checkSubscription: async () => {},
  startCheckout: async () => {},
  openCustomerPortal: async () => {},
});

export function PremiumProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [isPremium, setIsPremium] = useState(false);
  const [plan, setPlan] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [foundingMembersRemaining, setFoundingMembersRemaining] = useState(FOUNDING_CAP);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeSportName, setUpgradeSportName] = useState<string | null>(null);

  const checkSubscription = useCallback(async () => {
    if (!user) {
      setIsPremium(false);
      setPlan(null);
      setLoading(false);
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      if (error) throw error;
      setIsPremium(data.subscribed === true);
      setPlan(data.plan ?? null);
      setSubscriptionEnd(data.subscription_end ?? null);
      if (typeof data.founding_members_count === 'number') {
        setFoundingMembersRemaining(Math.max(0, FOUNDING_CAP - data.founding_members_count));
      }
      return data.subscribed === true;
    } catch (e) {
      console.error('check-subscription error:', e);
      try {
        const { data } = await supabase
          .from('founding_members_count' as any)
          .select('total')
          .single();
        if (data && typeof (data as any).total === 'number') {
          setFoundingMembersRemaining(Math.max(0, FOUNDING_CAP - (data as any).total));
        }
      } catch {}
      return false;
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    checkSubscription();
    const interval = setInterval(checkSubscription, 60000);
    return () => clearInterval(interval);
  }, [checkSubscription]);

  // Poll aggressively after checkout success to unlock Pro instantly
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') !== 'success') return;

    // Clean URL immediately
    window.history.replaceState({}, '', window.location.pathname);
    toast.info('Verifying your subscription...', { id: 'checkout-verify' });

    let attempts = 0;
    const maxAttempts = 10;

    const poll = async () => {
      attempts++;
      const subscribed = await checkSubscription();
      if (subscribed) {
        toast.success('🎉 Welcome to Sportara Pro! All features are now unlocked.', { id: 'checkout-verify', duration: 5000 });
        return;
      }
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        toast.info('Subscription is being processed. It will activate shortly!', { id: 'checkout-verify', duration: 5000 });
      }
    };

    // Start polling after a short initial delay
    setTimeout(poll, 1500);
  }, []);

  const startCheckout = useCallback(async (priceId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId },
      });
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        toast.error('Unable to start checkout. Please try again.');
      }
    } catch (e) {
      console.error('Checkout error:', e);
      toast.error('Something went wrong starting checkout. Please try again.');
    }
  }, []);

  const openCustomerPortal = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        toast.error('Unable to open subscription management. Please try again.');
      }
    } catch (e) {
      console.error('Customer portal error:', e);
      toast.error('Could not open subscription management. Please try again.');
    }
  }, []);

  return (
    <PremiumContext.Provider value={{
      isPremium,
      plan,
      subscriptionEnd,
      loading,
      foundingMembersRemaining,
      showUpgrade,
      setShowUpgrade,
      upgradeSportName,
      setUpgradeSportName,
      checkSubscription,
      startCheckout,
      openCustomerPortal,
    }}>
      {children}
    </PremiumContext.Provider>
  );
}

export const usePremium = () => useContext(PremiumContext);
